import { Form, Formik, Field } from "formik";
import * as Yup from "yup";
import { useState, useEffect } from "react";
import { MdLockOutline, MdVisibility, MdVisibilityOff, MdArrowBack } from "react-icons/md";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";

import logoImg from "../../assets/logo.png";
import { api } from "../../services/api";

interface ResetPasswordForm {
  password: string;
  confirmPassword: string;
}

const schema = Yup.object().shape({
  password: Yup.string()
    .min(6, "Senha deve ter pelo menos 6 caracteres")
    .required("Campo senha obrigatório"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Senhas devem ser iguais")
    .required("Campo confirmação de senha obrigatório"),
});

export function ResetPasswordConfirm() {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      enqueueSnackbar("Token inválido", { variant: "error" });
      navigate("/signin");
    }
  }, [token, navigate]);

  async function handleSubmit({ password }: ResetPasswordForm) {
    try {
      setIsLoading(true);

      await api.post("/reset-password", {
        token,
        password,
      });

      enqueueSnackbar("Senha redefinida com sucesso!", { variant: "success" });
      navigate("/signin");
    } catch (error) {
      enqueueSnackbar("Erro ao redefinir senha. Token pode ter expirado.", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#0085BD] via-[#006a96] to-[#004d6d]">
      {/* Background decorativo */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#92C163]/10 rounded-full blur-3xl" />
      </div>

      {/* Card */}
      <div className="relative w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 md:p-10">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-2xl p-4 shadow-lg">
              <img className="w-40 h-auto" src={logoImg} alt="Logo Luvilimp" />
            </div>
          </div>

          {/* Título */}
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Nova Senha
            </h1>
            <p className="text-white/70 text-sm">
              Digite sua nova senha para continuar
            </p>
          </div>

          {/* Formulário */}
          <Formik
            initialValues={{
              password: "",
              confirmPassword: "",
            }}
            validationSchema={schema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({ errors, touched }) => (
              <Form className="space-y-5">
                {/* Campo Nova Senha */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    Nova Senha
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <MdLockOutline className="text-white/50" size={20} />
                    </div>
                    <Field
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="••••••••"
                      className={`w-full h-14 pl-12 pr-12 bg-white/10 border ${
                        errors.password && touched.password
                          ? "border-red-400"
                          : "border-white/20 focus:border-white/50"
                      } rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/50 hover:text-white transition-colors"
                    >
                      {showPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                    </button>
                  </div>
                  {errors.password && touched.password && (
                    <p className="mt-1 text-red-300 text-xs">{errors.password}</p>
                  )}
                </div>

                {/* Campo Confirmar Senha */}
                <div>
                  <label className="block text-white/90 text-sm font-medium mb-2">
                    Confirmar Senha
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <MdLockOutline className="text-white/50" size={20} />
                    </div>
                    <Field
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="••••••••"
                      className={`w-full h-14 pl-12 pr-12 bg-white/10 border ${
                        errors.confirmPassword && touched.confirmPassword
                          ? "border-red-400"
                          : "border-white/20 focus:border-white/50"
                      } rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/50 hover:text-white transition-colors"
                    >
                      {showConfirmPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                    </button>
                  </div>
                  {errors.confirmPassword && touched.confirmPassword && (
                    <p className="mt-1 text-red-300 text-xs">{errors.confirmPassword}</p>
                  )}
                </div>

                {/* Botão Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-14 bg-[#92C163] hover:bg-[#7aab4e] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Redefinindo...
                    </>
                  ) : (
                    "Redefinir Senha"
                  )}
                </button>

                {/* Link Voltar */}
                <Link
                  to="/signin"
                  className="flex items-center justify-center gap-2 text-white/70 hover:text-white transition-colors text-sm"
                >
                  <MdArrowBack size={16} />
                  Voltar para o login
                </Link>
              </Form>
            )}
          </Formik>
        </div>

        {/* Footer */}
        <p className="text-center text-white/50 text-sm mt-6">
          © {new Date().getFullYear()} Luvilimp. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
